'use client';

import { useEffect } from 'react';

import { notifyAuthStateChanged } from '@shared/utils/authState';
import { getCookie } from '@shared/utils/cookies';
import { refreshAccessToken } from '@shared/utils/refreshAccessToken';

/**
 * accessToken(2h) 만 만료되고 refreshToken(30d) 은 살아있는 구간을 보정한다.
 *
 * 이 구간에서는 UI 가 로그인 상태로 보이지만 Authorization 헤더가 붙지 않아,
 * 사용자가 인증 API 를 건드리는 순간에야 401 → 갱신이 일어난다.
 * 진입/포커스 복귀 시점에 미리 갱신해 그 지연과 상태 불일치를 없앤다.
 *
 * - 갱신 성공: 쿠키 세트가 새로 발급돼 UI·요청 모두 로그인 상태로 복구
 * - 갱신 실패: BFF 가 인증 쿠키를 전부 삭제 → UI 가 로그아웃으로 전환
 * - 네트워크 오류: 쿠키 유지, 다음 기회에 재시도 (강제 로그아웃 없음)
 */
async function syncAuthSession(): Promise<void> {
  if (typeof document === 'undefined') return;
  // accessToken 이 남아있으면 갱신 불필요.
  if (getCookie('accessToken')) return;
  // 로그인 이력이 없으면 refreshToken 도 없어 시도할 의미가 없다.
  if (!getCookie('isLoggedIn')) return;

  await refreshAccessToken();
  notifyAuthStateChanged();
}

/**
 * 인증 세션 동기화 — AppProviders 에 마운트.
 *
 * 마운트 시점 1회 + 탭 포커스 복귀마다 실행. 탭을 켜둔 채 방치해
 * 로드가 다시 일어나지 않는 경우까지 커버하기 위해 포커스를 함께 본다.
 */
export function AuthSessionSync() {
  useEffect(() => {
    void syncAuthSession();

    const handleFocus = () => {
      void syncAuthSession();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void syncAuthSession();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return null;
}
